/**
 * Reusable CRM list primitives (iter-97).
 *
 *   <ActionMenu items={[{label, icon, onClick, destructive, hidden}]} />
 *     Replaces multiple action icons with a single ⋮ kebab dropdown.
 *
 *   <StatusBadge value="confirmed" tone="success" />
 *     Compact coloured pill.
 *
 *   <ChipFilter options={[{value, label, count}]} value onChange />
 *     Compact filter row using rounded chips instead of long labels.
 *
 *   <CrmListShell title actions search filters>
 *     Page header + filter bar with consistent spacing on white.
 *
 *   <ResponsiveTable columns rows mobileCard>
 *     Renders a real <table> on ≥ md, stacks into cards on < md.
 *     Each column may have hideOnMobile to keep mobile cards clean.
 */
import React from 'react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { MoreVertical } from 'lucide-react';
import { cn } from '../../lib/utils';

// ─── ActionMenu ───────────────────────────────────────────────
export const ActionMenu = ({ items = [], align = 'end', triggerLabel = 'Actions', size = 'sm', testid }) => {
  const visible = items.filter((i) => i && !i.hidden);
  if (visible.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          title={triggerLabel}
          className={cn(
            'inline-flex items-center justify-center rounded-md text-[#6B6B7A] hover:bg-[#FFFDF8] hover:text-[#1E1E24] transition-colors',
            size === 'sm' ? 'w-8 h-8' : 'w-9 h-9'
          )}
          data-testid={testid || 'row-action-menu'}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-48">
        {visible.map((it, idx) => {
          if (it.separator) return <DropdownMenuSeparator key={`sep-${idx}`} />;
          const Icon = it.icon;
          return (
            <DropdownMenuItem
              key={it.key || it.label}
              disabled={it.disabled}
              onClick={(e) => { e.stopPropagation(); it.onClick?.(e); }}
              className={cn(
                'text-sm cursor-pointer',
                it.destructive && 'text-[#FF3B6B] focus:text-[#FF3B6B] focus:bg-[#FF3B6B]/10'
              )}
              data-testid={it.testid || `row-action-${(it.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {Icon && <Icon className="w-3.5 h-3.5 mr-2 opacity-70" />}
              {it.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ─── StatusBadge ──────────────────────────────────────────────
const TONE_CLASS = {
  success: 'bg-[#06D6A0]/10 text-[#06D6A0]',
  warn: 'bg-[#FB923C]/10 text-[#FB923C]',
  danger: 'bg-[#FF3B6B]/10 text-[#FF3B6B]',
  muted: 'bg-[#E8E5DF] text-[#6B6B7A]',
  info: 'bg-[#A78BFA]/15 text-[#7C3AED]',
  brand: 'bg-[#FF3B6B]/10 text-[#FF3B6B]',
};
export const StatusBadge = ({ tone = 'muted', children, className, testid }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider',
      TONE_CLASS[tone] || TONE_CLASS.muted,
      className
    )}
    data-testid={testid}
  >
    {children}
  </span>
);

// ─── ChipFilter ───────────────────────────────────────────────
export const ChipFilter = ({ options = [], value, onChange, className, testid = 'chip-filter' }) => (
  <div className={cn('flex flex-wrap items-center gap-1.5', className)} data-testid={testid}>
    {options.map((opt) => {
      const active = (value || '') === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange?.(opt.value)}
          className={cn(
            'text-xs font-semibold px-2.5 py-1 rounded-full transition-colors',
            active
              ? 'bg-[#FF3B6B] text-white'
              : 'bg-[#FFFDF8] text-[#6B6B7A] hover:bg-[#FF3B6B]/10 border border-[#E8E5DF]'
          )}
          data-testid={`${testid}-${opt.value}`}
        >
          {opt.label}
          {opt.count !== undefined && (
            <span className={cn('ml-1.5 text-[10px] opacity-80', active && 'opacity-100')}>{opt.count}</span>
          )}
        </button>
      );
    })}
  </div>
);

// ─── CrmListShell ─────────────────────────────────────────────
export const CrmListShell = ({ title, subtitle, actions, search, filters, children, testid }) => (
  <div className="space-y-4" data-testid={testid}>
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          {title}
        </h2>
        {subtitle && <p className="text-xs sm:text-sm text-[#6B6B7A] mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
    {(search || filters) && (
      <div className="bg-white border border-[#E8E5DF] rounded-2xl p-3 flex flex-wrap items-center gap-2">
        {search}
        {filters}
      </div>
    )}
    <div className="bg-white border border-[#E8E5DF] rounded-2xl overflow-hidden">{children}</div>
  </div>
);

// ─── ResponsiveTable ──────────────────────────────────────────
// columns: [{key, header, render(row), align, hideOnMobile, className}]
// rows:    array of { ... } — each row should have a stable `id` field or pass `getKey`.
export const ResponsiveTable = ({ columns = [], rows = [], getKey, empty, onRowClick, testid = 'crm-table' }) => {
  if (!rows || rows.length === 0) {
    return <div className="px-6 py-10 text-center text-sm text-[#6B6B7A]" data-testid={`${testid}-empty`}>{empty || 'No items yet.'}</div>;
  }
  const keyFn = getKey || ((r, i) => r.id || r._id || r.booking_id || r.customer_id || r.user_id || r.gift_card_id || r.transaction_id || i);
  return (
    <>
      {/* Desktop / tablet — real table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testid}-table`}>
          <thead className="bg-[#FFFDF8] border-b border-[#E8E5DF]">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    'text-left text-[10px] uppercase tracking-widest font-semibold text-[#6B6B7A] px-4 py-2.5',
                    c.align === 'right' && 'text-right',
                    c.align === 'center' && 'text-center'
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={keyFn(row, idx)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-[#E8E5DF]/60 last:border-b-0',
                  onRowClick && 'cursor-pointer hover:bg-[#FFFDF8]'
                )}
                data-testid={`${testid}-row-${keyFn(row, idx)}`}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn(
                      'px-4 py-3 align-middle text-[#1E1E24]',
                      c.align === 'right' && 'text-right',
                      c.align === 'center' && 'text-center',
                      c.className
                    )}
                  >
                    {c.render ? c.render(row, idx) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile — cards (action menu top-right per user spec) */}
      <ul className="md:hidden divide-y divide-[#E8E5DF]" data-testid={`${testid}-mobile`}>
        {rows.map((row, idx) => {
          const visibleCols = columns.filter((c) => !c.hideOnMobile);
          // Find the actions column (if any)
          const actionCol = columns.find((c) => c.key === 'actions');
          const dataCols = visibleCols.filter((c) => c.key !== 'actions');
          return (
            <li
              key={keyFn(row, idx)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn('px-4 py-3', onRowClick && 'cursor-pointer hover:bg-[#FFFDF8]')}
              data-testid={`${testid}-card-${keyFn(row, idx)}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0 space-y-1">
                  {dataCols.map((c, i) => (
                    <div key={c.key} className={cn('flex flex-col', i === 0 && 'mb-0.5')}>
                      {i > 0 && (
                        <span className="text-[10px] uppercase tracking-widest text-[#6B6B7A] font-semibold">
                          {c.header}
                        </span>
                      )}
                      <div className={cn(i === 0 ? 'font-semibold text-[#1E1E24]' : 'text-sm text-[#1E1E24]', 'min-w-0 break-words')}>
                        {c.render ? c.render(row, idx) : row[c.key]}
                      </div>
                    </div>
                  ))}
                </div>
                {actionCol && (
                  <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                    {actionCol.render ? actionCol.render(row, idx) : null}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default CrmListShell;
